<template>
<div class="chapter">
<div class="inner_page">
<div class="image-wrap">
<div class="image">
<img alt="image" src="images/chap1_pagexii_fig1.jpg" style="max-width:100%"/>
<p class="caption">Figure 1.1 The rapid transition from steam to diesel-electric power after World War II
produced countless scenes like this one in Birmingham, Alabama. Antique 4-6-0 No.
7113, built by Baldwin in 1907 as Atlanta Birmingham &amp; Atlantic No. 113, provides a stark
contrast to purple-and-white GP7 No. 103. The colorful 1,500-hp Atlantic Coast Line
diesel was less than a year old when photographed in May 1951, but the steamer would be
scrapped within months. J. Parker Lamb.</p>
</div>
</div>
<h1 class="chap-number"><a id="page1"></a><a href="#/006_Contents#ch1" id="ch1"><span style="border-top:5px solid #808080;padding-top:0.2em">   <span class="small">CHAPTER 1</span>   </span></a></h1>
<h1 class="chap-title"><a href="#/006_Contents#ch1">PRECURSOR TECHNOLOGIES</a></h1>
<h2><b>Internal Combustion Engines</b></h2>
<p class="paraindent1">The transformation of thermal energy into mechanical power using a piston-cylinder
configuration was one of the fundamental concepts brought to practicality during the
Industrial Revolution of the nineteenth century. In these machines, piston movement is
produced by a working fluid that becomes highly energetic through a combustion process.
In the intervening centuries, two major types of reciprocating heat power machines have
seen widespread use. The earliest type was the steam engine in which the working fluid
(water) receives its thermal energy outside the power cylinder (in a boiler). A half-century
later, the level of technological development had progressed to the point where a second
type of reciprocating engine was possible. In this design, the working fluid (air) undergoes
an explosive combustion process inside the power cylinder. Hence the common names for
these two designs are based on whether the combustion occurs inside or outside the
cylinder.</p>
<p class="paraindent1">The first internal combustion engine concept was proposed in 1862 by Alphonse-Eugène
Beau de Rochas, who was later issued a French patent for a generic internal
ignition engine, although no actual machine was ever built. Even at this early stage,
theoretical calculations showed that such an engine would have a much higher thermal
efficiency than a conventional steam engine. This efficiency represents the proportion of
thermal energy in the working fluid that is converted to mechanical work.</p>
<p class="paraindent1">Credit for building the first working machine belongs to Nikolaus August Otto and his
partner, Eugen Langen of Cologne, Germany. Unaware of de Rochas’s patent, they developed
and demonstrated a single-cylinder spark-ignition engine at the Paris Exhibition in
1867. Otto and Langen then formed a company to produce these engines, and by 1877 they
had received an American patent and begun marketing them in the United States. The
scientific world has given Otto’s name to the subsequent design of all spark-ignition machines
(i.e., Otto cycle engines).</p>
<p class="paraindent1">Another pioneer in improving the spark engine was Gottlieb Daimler, who was superintendent
of Otto’s engine works in Germany. In 1879 he patented a multi-cylinder machine
with a crankshaft, and he soon teamed up with Wilhelm Maybach to produce the first
commercially successful automotive engine. Another German inventor, Carl Benz, perfected
a system for precise timing of ignition arcs from spark plugs. Of course, Daimler and
Benz would later form a German automotive company whose successors are still in <a id="page2">existence. Some estimate of the gestation period of engine technology can be made by
observing that, at the beginning of the twentieth century, after over 52,000 steam locomotives
had been produced in the United States, there were only 936 American automobiles
using spark-ignition engines.</a></p><a id="page2">
<p class="paraindent1">A second major type of IC engine was originated in the 1890s by French-born Rudolf
Diesel, who had received a doctoral degree from the engineering school in Munich in
1880. After some years of working on ice-making machines, his attention turned to the
elimination of electrically powered spark plugs in the Otto engine. Diesel’s concept involved
compressing the intake air to such a high pressure and temperature that, when
droplets of fuel were sprayed into the cylinder, a spontaneous combustion would occur. His
theoretical calculations demonstrated that the higher temperatures and pressures inside
the cylinder would produce a thermal efficiency even larger than that of the Otto engine.</p>
<p class="paraindent1">Early successes of the compression engine led to great wealth and acclaim for Diesel.
For example, in 1897 St. Louis brewer Adolphus Busch traveled to Germany and negotiated
the sale of American rights for the manufacture of his engine. The Busch-Sulzer
Brothers Diesel Engine Company began production in 1910. Dr. Diesel made two trips to
America in 1904 to visit the St. Louis Exposition, where he met with Busch and Edward H.
Harriman of the Union Pacific. Some reports suggest that Diesel convinced Harriman to
construct an experimental diesel locomotive. Although the trade press announced the
testing, no further information was ever published. On his 1912 trip, Diesel met with
Thomas A. Edison and other luminaries and gave an address to the American Society of
Mechanical Engineers in which he chided Americans for being too timid in exploiting the
advantages of his new engine concept. Had he lived a normal life span, he would have
been more than vindicated, but the work of this eccentric genius was cut short in 1913
when, in poor health and despondent over recent business failures, he disappeared during
a nighttime ferry crossing of the English Channel (a probable suicide). Subsequent development
of the compression-ignition engine lagged far behind the Otto design because
the former required a level of materials development and manufacturing precision that
was still two decades in the future.</p>
</a><p class="paraindent1"><a id="page2">Because these two IC engine designs lie at the heart of diesel locomotive evolution, it is
desirable to describe some thermodynamic features of each, using a pressure-volume
diagram (P-V) to depict schematically the physical processes occurring inside the cylinder.
In </a><a href="#/#chart1_1">chart 1.1</a>, which illustrates the operation of a spark-ignition engine, the top-most position
of the piston is known as top dead center (TDC), and the bottom limit is the bottom dead
center (BDC). The volume being measured on the horizontal scale is the space above the
top of the piston at each instant. Each passage of the piston between top and bottom is
known as a stroke. The directional arrows indicate two strokes in each direction. Thus this
sequence of operations is known as a four-stroke cycle Otto engine and more commonly as
a four-cycle Otto engine.</p>
<p class="paraindent1">The diagram identifies six events:</p>
<p class="list-item1 top">1 to A   Intake stroke to pull air into the cylinder (intake valves open).</p>
<p class="list-item1">A to B   Compression stroke to raise pressure and temperature of fuel-laden air.</p>
<p class="list-item1">B to C   Spark ignition to further energize the fuel-air mixture.</p>
<p class="list-item1">C to D   Power stroke to rotate crankshaft.</p>
<p class="list-item1">D to A   Pressure decreases as exhaust valves open.</p>
<p class="list-item1">A to 1   Scavenge stroke to remove combustion gas from cylinder.</p>
<p class="paraindent1 top">The four-stroke cycle requires two revolutions of the crankshaft for each power stroke.
However, many engine designs are able to eliminate the first and last processes (represented
by the dashed line in <a href="#/#chart1_1">chart 1.1</a>) and thus produce a two-stroke cycle engine. The
applications of both designs to locomotive engines will be discussed later.</p>
<p class="paraindent1">A similar P-V diagram of Diesel’s original cycle is presented in <a href="#/#chart1_2">chart 1.2a</a>. The general <a id="page3">shapes of the two diagrams differ only between B–C, which depicts a constant pressure
ignition. Also, it must be remembered that the maximum pressure in a diesel cycle can be
at least two times higher than that in an Otto cycle. Moreover, the compression ratio
(volume at BDC divided by that at TDC) is also much larger for the diesel cycle. While
this diagram displays the operation of an early low-rpm diesel, one must represent a
contemporary high-speed engine using some aspects of the Otto cycle, as shown in </a><a href="#/#chart1_2">chart
1.2b</a>, where it is seen that the ignition begins during a short period of constant volume near
TDC. This is often denoted as a mixed cycle or mixed diesel cycle.</p>
<div class="image-wrap" id="chart1_1">
<div class="image">
<img alt="image" src="images/chap1_page3_char1.jpg" style="max-width:100%"/>
<p class="caption">Chart 1.1. The four power-producing
processes inside
the cylinder of a spark-ignition
engine (Otto cycle)
are depicted schematically
on a pressure-volume
diagram. The pressure
variation is that which exists
at the top of the piston as
it makes an upward stroke
from bottom dead center to
top dead center and return.
The solid path characterizes
a two-stroke cycle while the
dashed line shows the four-stroke
cycle. Howell and
Buckius, <i>Engineering
Thermodynamics.</i></p>
</div>
</div>
<p class="paraindent1">A major caveat in these P-V diagrams is that they represent idealized conditions. In
particular, the sharp corners at the intersection of two processes would certainly not be
realistic. Thus a measured pressure variation inside the cylinder would display rounded
corners at these intersection points. However, the simple theoretical depictions are effective
as indicators of engine characteristics.</p>
<h2><b>Electrical Machinery</b></h2>
<p class="paraindent1">The basic principle by which an electric motor operates is magnetism, first discovered
by the English physicist Michael Faraday in the 1820s. Although some materials are
naturally and permanently magnetic, he showed that an electric current passing through a
conducting material would also produce a surrounding electromagnetic field. An important
property of magnets is their polarity (normally called north and south poles and noted
as + or -). Magnets having the same polarity will repel one another whereas opposing
polarities will attract. Soon after Faraday’s discoveries, Joseph Henry, a college instructor in
Albany, New York, developed a practical electromagnet and used it to power an experimental
motor that employed attraction and repulsion to produce a reciprocating motion
that was then transformed into rotating motion (just as in an early steam engine).</p>
<p class="paraindent1">The first electric motors of significance were based on the current produced by a metalacid  battery, the first electrical storage device. Batteries operate through an electrochemical
action that occurs when metal electrodes react with the surrounding acid. This results
in a steady migration of negatively charged subatomic particles (electrons) from one side of
the battery to the other, thus creating a voltage difference and consequent current flow
between anode and cathode (+ and - electrodes). Since battery current is steady and
unidirectional, it was designated as direct current (DC). Around 1840 Robert Davidson, an
electrical inventor in Aberdeen, Scotland, completed a 5-ton locomotive powered by 40
zinc-iron sulfuric acid batteries, while the pinnacle of battery-powered trains came in 1851
when Dr. Charles G. Page constructed a 16-hp locomotive powered by 100 battery cells.
The reciprocating action of the magnets drove a flywheel that allowed the train to reach 19
mph on a 5-mile line between Washington, D.C., and Bladensburg, Maryland.</p>
<div class="image-wrap" id="chart1_2">
<div class="image">
<img alt="image" src="images/chap1_page4_char2.jpg" style="max-width:100%"/>
<p class="caption"><a id="page4">Chart 1.2. Pressure-volume
diagram (at top) for a low-rpm
compression-ignition
engine (diesel cycle)
features much higher peak
pressures and temperatures
than a spark-ignition engine
with the same bore. These
conditions produce ignition
as the fuel is injected at
point B. Both two-stroke and
four-stroke cycle operations
are indicated. At bottom is a
corresponding diagram for
a modern high-rpm diesel
engine, in which ignition is
initiated slightly before peak
pressure. Howell and
Buckius, <i>Engineering
Thermodynamics.</i></a></p><a id="page4">
</a></div><a id="page4">
</a></div><a id="page4">
</a><div class="image-wrap" id="chart1_3"><a id="page4">
</a><div class="image"><a id="page4">
<img alt="image" src="images/chap1_page5_char3.jpg" style="max-width:100%"/>
</a><p class="caption"><a id="page4"></a><a id="page5">Chart 1.3. Fundamental
elements of a two-pole direct
current motor include the
field magnet and the armature,
which contains the
commutator that is necessary
to produce a unidirectional
current flow.</a></p><a id="page5">
</a></div><a id="page5">
</a></div><a id="page5">
<p class="paraindent1">The first practical DC motor was built in 1860 by Italian engineer Antonio Pacinotti
utilizing an unusual ring armature. An even more important discovery was made in 1873
when Zénobe Théophile Gramme of Belgium demonstrated that the components of a
DC motor and a DC generator were identical. In one case the machine received mechanical
power (shaft) and produced a DC output whereas, if it received DC input, it produced
mechanical power via a rotating shaft.</p>
</a><p class="paraindent1"><a id="page5">The sketch in </a><a href="#/#chart1_3">chart 1.3</a> illustrates the rudimentary elements of a DC generator. Between
the two magnetic poles (N and S) there exists an invisible electromagnetic force field.
When a conducting wire passes through this field at a certain orientation, it is said to be
cutting lines of magnetic flux (force). The result is an electric current in the conductor
wire. By mounting the conducting loop on a rotating shaft (armature), one can transmit
the generated current away from the machine using a commutator, illustrated by the split
ring and the two electric brushes (rubbing contacts). Note that this is a simple two-pole
machine with two segments in the commutator ring. Most traction motors have four poles,
but other applications may have more. In addition, industrial-sized electromagnets generally
consist of metal slabs wrapped by coils of wire known as field coils or field windings.</p>
<p class="paraindent1">With the development of reliable DC motors, the era of electric locomotives began in <a id="page6">1879 when Dr. Ernst Werner von Siemens built a demonstration locomotive for the Berlin
Exhibition. Running on a half-meter gauge track, the tiny train hauled 100,000 people
during the exhibition. This demonstration mirrored a similar event almost 50 years earlier
(1831) when Matthias Baldwin demonstrated his first steam locomotive with a miniature
train operating inside a large museum building in Philadelphia. Three years after Siemens’s
demonstration in Berlin, Thomas Edison laid 1,400 feet of track at his Menlo Park,
New Jersey, laboratories and developed a small DC locomotive that pulled two cars at 40
mph.</a></p><a id="page6">
<p class="paraindent1">Within a few decades it became obvious that steam-powered commuter and elevated
lines were producing widespread urban pollution, and there began a national effort to
utilize these newly developed electric trains, which were both cleaner and quieter than
smoke-belching steamers (even if they were tiny 0-4-2 Forney tank engines). Frank J.
Sprague was one of the early engineering leaders of electric-powered railway equipment.
In 1885 he developed a prototype for an electric-powered train using elevated trackage of
the Manhattan Railway. Three years later, he constructed the Richmond Union Passenger
Railway, the nation’s first all-electric street rail system. He also developed the first front-hung
traction motors mounted on a pivoted truck and geared to driving axle, and he later
invented a method of coupling two electrically driven cars under one controller (the
multiple unit connection).</p>
<p class="paraindent1">Finally in 1894 General Electric’s Lynn, Massachusetts, plant constructed the first
power unit designed for regular railroad service, an experimental single-truck 30-ton model
with traction motors on each axle. It was exhibited at the World’s Colombian Exhibition in
Chicago in 1896 and then put to work for an industrial railroad in New Haven, Connecticut.
The next GE model was the nation’s first steeple cab design, a 35-ton B-B unit that used
four 500-volt DC traction motors to produce a drawbar pull of 14,000 pounds. It was
eventually sold to an industrial mill and used until 1964!</p>
<p class="paraindent1">As DC propulsion advanced, the proper method of controlling traction motors for varying
speed and varying loads became important. The physical basis for motor control begins
with the relationship between voltage, current, and resistance. This can be written as</p>
<p class="paracenter">Voltage = Current × Resistance</p>
<p class="paranoindent1">where current is measured in amperes and resistance in ohms. This is known as Ohm’s
Law for circuits and can be applied to an entire circuit or to any element within the circuit.
Symbolically this can be written as</p>
<p class="paracenter">E = I R</p>
<p class="paranoindent1">Moreover, electric power can be expressed as</p>
<p class="paracenter">Power = Voltage × Current</p>
<p class="paranoindent1">or</p>
<p class="paracenter">P = E I = I<sup>2</sup> R</p>
<p class="paranoindent1">Power is measured in volt-amperes, usually kilovolt-amperes (kVa). Power is also much
more dependent on current levels than voltage.</p>
</a><p class="paraindent1"><a id="page6">The DC motor’s two components, armature and field coil, have different electrical
resistances. Moreover, there are two choices for connecting these components, as shown
in </a><a href="#/#chart1_4">chart 1.4</a>. In a DC-series motor they are connected end to end, while in a DC-shunt
motor they are connected in parallel. For a series motor, the total resistance of the two
elements is merely the sum of the component resistances, and therefore, according to
Ohm’s Law, the voltage across each element will be different, although electrical continuity
requires that the current through each element be the same. In contrast, with a
parallel circuit, the full voltage acts across each component, thus producing different
currents through each part, depending on its resistance.</p>
<div class="image-wrap" id="chart1_4">
<div class="image">
<img alt="image" src="images/chap1_page7_char4.jpg" style="max-width:100%"/>
<p class="caption"><a id="page7">Chart 1.4. The field and
armature of a DC motor
may be connected in
either series or parallel
arrangements, each of
which has distinct operating
characteristics. Middleton,
<i>When the Steam Roads
Electrified.</i></a></p><a id="page7">
</a></div><a id="page7">
</a></div><a id="page7">
</a><p class="paraindent1"><a id="page7">Early experimentation showed that the DC-series arrangement was the most desirable
for traction motor applications. As the load (current) on the motor increases, the magnetic
field strength automatically increases, and the result is an increase in torque with a decrease
in speed. Consequently, the series motor tends to adjust itself readily to the varying
tractive effort required to accelerate and decelerate a train. In addition, further control can
be obtained by grouping DC -series motors themselves in either series or parallel circuits
or even in a hybrid series-parallel arrangement. These three circuits are depicted in
</a><a href="#/#chart1_5">chart 1.5</a>, where four traction motors receive power from a generator. In one circuit, all
motors are in series and therefore each operates on one-fourth of the total voltage of the
generator. In the full parallel circuit, the total voltage of the generator is imposed on each
motor, while the hybrid circuit allows each pair of motors to operate on half of the total
voltage. For early locomotives, the series circuit was used for starting, with transition to the
series-parallel at intermediate speeds and to the full parallel at high speeds. Moreover, the
transition was a manual two-handed procedure. Later machines included a fully automatic
transition control, while most modern DC machines use only two motor circuits,
parallel-shunt and full parallel, where the former is a modification of the series-parallel
connection in which part of the current is shunted from the field to the armature to
increase power.</p>
<p class="paraindent1">While General Electric surged ahead of everyone else in developing DC machinery
after the 1880s, its major rival became engaged in a crusade led by its determined owner,
George Westinghouse, who strongly favored the newer form of electricity, alternating
current (AC). As its name implies, the current and voltage in an AC system change
direction many times each second, and thus AC is characterized by an oscillation frequency
measured in cycles per second (cps). One cps is denoted as one Hertz in contemporary
literature, while one Megahertz equals one million cycles per second.</p>
<p class="paraindent1">The creation of the first AC machines occurred in the 1880s. Lucien Gaulard and John
D. Gibbs determined that one of AC’s significant benefits was that it permitted a variation
in voltage by use of a transformer, a device with no moving parts. This result led to the
design of long-distance transmission lines for electric utilities and some electrified railroads.
The remarkably simple transformer consists of two closely spaced iron cores (rods)
that are wound with insulated wires. These windings are designated as primary (input) and
secondary (output) coils. Early research showed that the difference in voltage between the
input and output is determined solely by the number of turns (loops) on each coil.</p>
<p class="paraindent1">For example, to increase the input voltage by ten times, the secondary coil (output)
would have ten times as many turns as the primary. The basic principle involved is the
same as mentioned earlier in connection with the simple DC motor, namely, a conductor
cutting through magnetic lines of force. Thus the instantaneous voltage over one cycle of
an AC signal rises from zero to a maximum value, then collapses to zero again before
changing directions and repeating this up-and-down excursion. The number of increases
and decreases in the primary voltage signal is multiplied by ten due to the larger number of
secondary windings, thus producing a tenfold increase in output voltage. However, since
no power is generated inside the transformer, we see that product of voltage and current is the same for both primary and secondary coils. Since voltage in the secondary is ten
times larger than in the primary coils, the secondary current is ten times smaller. This
is a significant advantage for long-distance transmission lines, inasmuch as the power
loss equals the resistance of the line multiplied by the value of current (raised to the
second power).</p>
<div class="image-wrap" id="chart1_5">
<div class="image">
<img alt="image" src="images/chap1_page8_char5.jpg" style="max-width:100%"/>
<p class="caption"><a id="page8">Chart 1.5. Series-connected
DC traction motors can
receive power from the
generator through series,
parallel, or hybrid
connections. Each
arrangement provides
performance characteristics
suitable for different speed
ranges. Middleton, <i>When
the Steam Roads
Electrified.</i></a></p><a id="page8">
</a></div><a id="page8">
</a></div><a id="page8">
<p class="paraindent1">As an example, we consider a central generating station that produces a voltage of 1,000
(1 kV). A transformer then steps up the voltage to 10,000 volts (10 kV) and at the same time
lowers the current by ten times before sending it out on the transmission line. Upon arrival
at its destination, a step-down transformer converts it to a low voltage, high current signal
that is useful locally.</p>
<p class="paraindent1">George Westinghouse was a brilliant engineer who gained early recognition and wealth
for his invention and development of pneumatic brakes for railroad rolling stock. He
entered the electrical field soon after the Gaulard-Gibbs discoveries, and in 1885 he
acquired U.S. patent rights for their designs. Only a year later, the Westinghouse Electric
and Manufacturing Company completed the first commercial AC power-generating station
in Buffalo, New York.</p>
<p class="paraindent1">Recruiting a staff of stellar engineers that included the Croatian genius Nikola Tesla,
Westinghouse Electric became the nation’s leader in AC technology and a natural foe of
Edison Electric Company (and its successor General Electric), which generally favored
DC equipment. Eventually, both companies licensed each other’s technology and were
the dominant builders of electrical equipment. Westinghouse entered the railroad field in
1895, joining forces with Baldwin Locomotive Works to produce a small boxcab B-B unit
(DC) weighing 46 tons.</p>
</a><p class="paraindent1"><a id="page8">As will be apparent in our discussion of diesel locomotive evolution, American manufacturing </a><a id="page9">in the early twentieth century was dominated by two sectors, one with a mechanical
orientation and the other emphasizing electrical machines and devices. The
diesel-electric locomotive demanded expertise in both areas, and many existing companies
were unable to develop such technical crossbreeding.</a></p><a id="page9">
<h2><b>Locomotive Fundamentals</b></h2>
<p class="paraindent1">Following is some general background information that will assist in understanding
subsequent discussions of locomotive development.</p>
<p class="paraindent1"><i>Engine configurations</i>—The first IC engines had but one cylinder, but for commercial
uses (e.g., automotive), at least two and preferably four cylinders were needed. Multiple
cylinders required a long crankshaft that converted piston oscillations into rotating motion,
in much the same way as a crankpin on the main driver of a steam locomotive. A slender
rod, with a bearing at each end, connected the crankshaft to each piston. The lower ends of
the connecting rods, as well as the crankshaft, were often designed to splash in the oil pan
sump, which also included a pump to keep all bearings lubricated. At the top of the
cylinders was a heavy cover (cylinder head) that sealed off the explosive combustion
process, which occurred many times per minute in each cylinder.</p>
<p class="paraindent1">Most IC engines use one of the following layouts of cylinders in relation to the crankshaft.
The simplest one is an in-line configuration wherein all cylinders are aligned vertically
above the crankshaft. The disadvantage of this layout is that, for more than eight
cylinders, it produces long machines with slender crankshafts that are prone to transverse
vibrations and fatigue cracking. Thus many designers began using a Vee-pattern in which
the cylinders are mounted at a small angle (to vertical), thereby allowing them to be much
closer longitudinally and producing shorter engines and crankshafts. While some early
locomotive engines were inline models, most of those built after World War II are V-12s
and V-16s.</p>
<p class="paraindent1">An uncommon design for railroad applications but popular in other applications was
the opposed-piston (O-P) arrangement, which appears to be two in-line engines stacked
one above the other (head to head), so that there are two crankshafts, one at the bottom and
another at the top. Therefore, each cylinder contains two pistons that oscillate in opposite
directions. Major advantages of the O-P design are that it produces the maximum power in
a given volume and automatically operates on a two-stroke cycle. It needs no valve control
mechanism, since one piston serves as the inlet and outlet valve for the other. A fourth
engine layout, used only in cases of limited space and small power requirements, is known
as a pancake configuration wherein the cylinders are lined up horizontally on each side of
the crankshaft. Schematically, it is the configuration that results if the angle between
cylinders of a Vee-layout is increased to 180 degrees. Such a design has been used often in
small automobiles, airplanes, and boats.</p>
</a><p class="paraindent1"><a id="page9"><i>Engine performance</i>—The following discussions will often include only two pieces of
information about the output of an IC engine, horsepower and rpm, even though there are
four critical measures of engine output. These are compression ratio (defined in relation to
</a><a href="#/#chart1_2">chart 1.2</a>), cylinder displacement (volume displaced by piston head during one stroke), rpm
(number of power strokes per second or minute), and finally the form of the aspiration
process (how incoming air enters the cylinder). Greater engine power can be obtained by
increasing compression ratio, displacement, and rpm. Additional power is also possible by
providing augmented aspiration so as to increase the density of air entering the cylinder.
With such heavier air, the piston will be able to do more work on each power stroke and
hence output is increased.</p>
<p class="paraindent1">A simple theoretical analysis identifies an important figure of merit for engine performance.
Known as the Mean Effective Pressure (MEP), it is proportional to the compression
ratio and the density of incoming air (aspiration), and it is related to engine output in the following way. The mechanical work done during one stroke of the piston can be
expressed as the product of the length of the stroke and a hypothetical mean effective force
(MEF) that exits during the stroke. However, the MEF equals the MEP times the area of
the piston head, so that mechanical work is</p>
<p class="paracenter">Work (per stroke) = MEP × [Area × Stroke]</p>
<div class="image-wrap" id="chart1_6">
<div class="image">
<img alt="image" src="images/chap1_page10_char6.jpg" style="max-width:100%"/>
<p class="caption"><a id="page10">Chart 1.6. Variation of
locomotive tractive effort
(pulling force) with speed
for two types of DC units,
illustrating the effects of
adhesion limits and
additional motors. Marre
and Pinkepank, <i>The
Contemporary Diesel
Spotter’s Guide.</i></a></p><a id="page10">
</a></div><a id="page10">
</a></div><a id="page10">
<p class="paranoindent1">The terms in brackets are equal to the engine displacement, a fixed value. This means that
the power produced by one piston equals the work (per stroke) multiplied by the number of
power strokes per minute. This latter number is proportional to the rpm and the type of
engine (two-stroke or four-stroke cycle). Therefore, engine power is proportional to rpm,
engine displacement, and MEP, with the latter being a function of compression ratio and
aspiration. Engineering literature usually employs the term <i>Brake Mean Effective Pressure</i>
(BMEP) to denote the value of MEP computed from actual performance tests.</p>
</a><p class="paraindent1"><a id="page10">Only a four-stroke cycle engine can operate solely with the suction of outside air during
the downward (intake) stroke. The simplest method of augmentation is to push more air
into the cylinder with a blower (high-pressure fan) that provides a small level of pressure
increase above atmospheric. Most early locomotive engines used this technique. The
largest rate of augmentation is provided by a supercharger (compressor), which can be
either gear-driven or powered by a turbine operating from hot exhaust gas. It is also
commonly used in locomotive applications. The supercharging process has virtually no
effect on the shape of the P-V diagrams in </a><a href="#/#chart1_2">chart 1.2</a>. However, the entire cycle operates at <a id="page11">larger pressures than does a normally aspirated engine. As a historical footnote, supercharged
aircraft engines saw major development during World War II in order for bombers
to fly at higher altitudes (where the air density is low) and thus were less vulnerable to
ground-based artillery.</a></p><a id="page11">
<p class="paraindent1"><i>Locomotive Performance</i>—Identification of the running gear of electrically powered
railroad trucks, irrespective of the source of power, is based on a European system that
counts axles, unlike the Whyte system for steam locomotives, which counts wheels. Powered
axles are identified by letters, whereas unpowered ones (called idlers) are numbered.
The letters A, B, C, and D represent one to four axles, respectively. The most common
power design, a two-axle, two-motor configuration, is a B-truck. In early motor cars, only
the forward truck was powered, thus its running gear was designated as B-2. Most locomotives
rode a pair of similar trucks, B-B or C-C, where the dash indicates a connecting
locomotive frame. In other cases, notably for passenger locomotives, a three-axle power
truck was used for a smoother ride at high speed, although the light tonnage required only
two powered axles. This led to the A1A–A1A configuration. While the foregoing arrangements
were used most often during the early years of diesel evolution, many other combinations
were employed occasionally. These included B-1, B-2, B-A1A, D-D, and B+B-B+B
where the plus sign indicates the locomotive main frame riding on two pair of
B-trucks, each pair being connected with a bolster.</p>
</a><p class="paraindent1"><a id="page11">The importance of the number of powered axles is shown in </a><a href="#/#chart1_6">chart 1.6</a>, which presents
the variation of tractive effort with locomotive speed. Both four-motor and six-motor
locomotives (B and C trucks) are included, along with two locomotive weights. It is also
evident that, while the additional motors of the C-trucks provide a substantially higher
tractive effort below 20 mph, they provide much less augmentation above 40 mph. However,
the larger locomotives could pull more at low speed and thus accelerate faster.
Because of their greater versatility, they have been more popular than B-B units on some
railroads.</p>
<p class="paraindent1">Another important aspect of performance from <a href="#/#chart1_6">chart 1.6</a> is the adhesion values, which
determine the maximum tractive effort as a percentage of the total weight on the driving
axles. If the locomotive attempts to pull more load than is allowed by the adhesion limit,
the wheels will slip and suddenly lose all power. The usual adhesion limit for dry rail is 0.25
as indicated by the 30-ton pull for a 120-ton locomotive. By sanding the rails, this value can
be improved to 30 percent. Since the largest tractive effort occurs just before wheel slip,
modern locomotives include wheel-slip control systems that use sensitive electronic measurements
to detect any small difference between the motion of the ground and that of the
wheel rim. For normal operation, both of these must be the same. But as the wheel begins
to slip, it speeds up instantly. By detecting an incipient slipping condition with highly
accurate measurements, the wheel-slip control system slows the wheel speed by short
interruptions of current to the motors. These systems permit overall adhesion values over
the 30 percent maximum shown. The latest truck designs have resulted in even further
advances in adhesion (see <span href="#/019_Chapter11#ch11" title="Link target outside of Sample">chapter 11</span>).</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 009_Chapter1.vue?ed8b4922